import React, { useCallback, useEffect, useRef, useState } from 'react';

import { ICONS } from '@/constants/icons';
import i18n from '@/i18n';

import AppButton from '../buttons/AppButton';
import Icon from '../icon/Icon';

import styles from './PhotoGallery.module.scss';

const THUMB_SIZE = { sm: 40, md: 88, lg: 140 };

/**
 * Griglia di miniature con anteprima ingrandita al click.
 *
 * Widget generico: riceve foto già risolte (`url` pronto) e non sa nulla del
 * dominio che gliele passa. Il lightbox è montato in-place e sta sopra un
 * `Dialog` MUI, così funziona anche dentro un modale.
 *
 * @param {{id?: string, url: string|null, alt?: string}[]} props.photos
 * @param {boolean} [props.loading]  risoluzione url in corso
 * @param {'sm'|'md'|'lg'} [props.size]  lato della miniatura
 * @param {number} [props.max]  quante miniature mostrare (il resto sotto "+N",
 *   che apre comunque l'anteprima su tutte)
 * @param {number} [props.total]  quante foto ha davvero l'entità, quando il
 *   chiamante ne passa solo una parte: l'ultima miniatura mostra "+N"
 * @param {(photo: object) => void} [props.onRemove]  se presente, ogni
 *   miniatura ha il bottone di rimozione
 * @param {string} [props.emptyLabel]  testo quando non c'è nessuna foto
 *   (se omesso, con zero foto il componente non renderizza nulla)
 */
export default function PhotoGallery({
  photos = [],
  loading = false,
  size = 'md',
  max,
  total,
  onRemove,
  emptyLabel,
}) {
  const [previewIndex, setPreviewIndex] = useState(null);

  const count = photos.length;
  const close = useCallback(() => setPreviewIndex(null), []);
  const step = useCallback(
    (delta) => setPreviewIndex((i) => (i === null ? i : (i + delta + count) % count)),
    [count]
  );

  if (!count) {
    if (loading) {
      return (
        <div className={styles.empty}>
          {i18n.t('caricamento_foto', { defaultValue: 'Caricamento foto…' })}
        </div>
      );
    }
    return emptyLabel ? <div className={styles.empty}>{emptyLabel}</div> : null;
  }

  const visible = max ? photos.slice(0, max) : photos;
  const hidden = Math.max(total ?? count, count) - visible.length;

  return (
    <>
      <div className={styles.gallery} style={{ '--thumb-size': `${THUMB_SIZE[size] ?? 88}px` }}>
        {visible.map((photo, index) => {
          const isLast = index === visible.length - 1;
          const label = photo.alt || i18n.t('apri_foto', { defaultValue: 'Apri foto' });
          return (
            <div key={photo.id || photo.url || index} className={styles.thumb}>
              <button
                type="button"
                className={`${styles.thumbOpen} ${photo.url ? '' : styles.thumbMissing}`}
                onClick={photo.url ? () => setPreviewIndex(index) : undefined}
                disabled={!photo.url}
                aria-label={label}
                title={
                  photo.url
                    ? label
                    : i18n.t('foto_non_disponibile', { defaultValue: 'Foto non disponibile' })
                }
              >
                {photo.url ? (
                  <img src={photo.url} alt={photo.alt || ''} loading="lazy" />
                ) : (
                  <Icon name="fileImage" />
                )}
                {isLast && hidden > 0 && (
                  <span className={styles.more} aria-hidden="true">
                    +{hidden}
                  </span>
                )}
              </button>
              {onRemove && (
                <AppButton
                  mode="icon"
                  variant="danger"
                  size="sm"
                  className={styles.remove}
                  icon={ICONS.close}
                  title={i18n.t('rimuovi', { defaultValue: 'Rimuovi' })}
                  onClick={() => onRemove(photo)}
                />
              )}
            </div>
          );
        })}
      </div>

      {previewIndex !== null && (
        <Lightbox photos={photos} index={previewIndex} onClose={close} onStep={step} />
      )}
    </>
  );
}

function Lightbox({ photos, index, onClose, onStep }) {
  const ref = useRef(null);
  const photo = photos[index];
  const many = photos.length > 1;

  // Il lightbox si apre da un click sulla miniatura: senza spostarci il focus,
  // Esc e le frecce resterebbero sul bottone sottostante.
  useEffect(() => {
    ref.current?.focus();
  }, []);

  const onKeyDown = (e) => {
    if (e.key === 'Escape') onClose();
    else if (e.key === 'ArrowLeft' && many) onStep(-1);
    else if (e.key === 'ArrowRight' && many) onStep(1);
    else return;
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <div
      ref={ref}
      className={styles.lightbox}
      role="dialog"
      aria-modal="true"
      aria-label={photo.alt || i18n.t('anteprima_foto', { defaultValue: 'Anteprima foto' })}
      tabIndex={-1}
      onKeyDown={onKeyDown}
    >
      {/* Il fondo è un bottone a tutta pagina: cliccare fuori dall'immagine
          chiude, senza appendere un handler al contenitore (e quindi senza
          doverlo neutralizzare sull'immagine). */}
      <button
        type="button"
        className={styles.backdrop}
        aria-label={i18n.t('chiudi_anteprima', { defaultValue: 'Chiudi anteprima' })}
        tabIndex={-1}
        onClick={onClose}
      />

      <LightboxButton
        className={styles.lightboxClose}
        icon={ICONS.close}
        label={i18n.t('chiudi_anteprima', { defaultValue: 'Chiudi anteprima' })}
        onClick={onClose}
      />

      {many && (
        <LightboxButton
          className={styles.navPrev}
          icon={ICONS.chevronLeft}
          label={i18n.t('foto_precedente', { defaultValue: 'Foto precedente' })}
          onClick={() => onStep(-1)}
        />
      )}

      <img src={photo.url} alt={photo.alt || ''} />

      {many && (
        <LightboxButton
          className={styles.navNext}
          icon={ICONS.chevronRight}
          label={i18n.t('foto_successiva', { defaultValue: 'Foto successiva' })}
          onClick={() => onStep(1)}
        />
      )}

      {many && (
        <span className={styles.lightboxCounter}>
          {index + 1} / {photos.length}
        </span>
      )}
    </div>
  );
}

/** Comando del lightbox: chiaro su fondo scuro, fuori dalle varianti AppButton. */
function LightboxButton({ className, icon, label, onClick }) {
  return (
    <button
      type="button"
      className={`${styles.lightboxBtn} ${className}`}
      title={label}
      aria-label={label}
      onClick={onClick}
    >
      <i className={icon} aria-hidden="true" />
    </button>
  );
}
